import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { API_BASE_URL } from '../../utils/api'
import { BaseWidget } from './BaseWidget'
import { TrendingUpIcon, TrendingDownIcon, CalendarIcon } from '../Icons'

interface YearlyStatisticsWidgetProps {
  className?: string
  widgetId?: string
}

interface YearStat {
  year: number
  totalPaidCommission: number
  totalLoanVolume: number
  totalDealsCount: number
  conversionRate: number
  totalLeadsCreated: number
  totalDealsCreated: number
}

interface YearlyStatsData {
  years: YearStat[]
}

const YearlyStatisticsWidget: React.FC<YearlyStatisticsWidgetProps> = ({ className, widgetId }) => {
  const { t } = useTranslation()
  const [stats, setStats] = useState<YearlyStatsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedYear, setSelectedYear] = useState<number | null>(null)
  const [isMinimal, setIsMinimal] = useState(false)

  // Detect widget height for minimal display
  useEffect(() => {
    const checkHeight = () => {
      const widgetElement = document.querySelector(`[data-widget-id="${widgetId || 'yearly_statistics'}"]`)
      if (widgetElement) {
        const height = widgetElement.clientHeight
        // Trigger minimal display at 340px or less (h:2 grid units)
        setIsMinimal(height <= 340)
      }
    }
    
    checkHeight()
    // Add a small delay to ensure proper rendering
    const timer = setTimeout(checkHeight, 100)
    window.addEventListener('resize', checkHeight)
    const observer = new MutationObserver(checkHeight)
    const widgetElement = document.querySelector(`[data-widget-id="${widgetId || 'yearly_statistics'}"]`)
    if (widgetElement) {
      observer.observe(widgetElement, { attributes: true, attributeFilter: ['style'] })
    }
    
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', checkHeight)
      observer.disconnect()
    }
  }, [widgetId])

  const formatAmount = (amount: number) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M$`
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K$`
    }
    return `$${amount.toLocaleString()}`
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const calculateGrowth = (current: number, previous: number): { percentage: number; isPositive: boolean } => {
    if (previous === 0) {
      return { percentage: current > 0 ? 100 : 0, isPositive: current > 0 }
    }
    const growth = ((current - previous) / previous) * 100
    return { percentage: Math.abs(growth), isPositive: growth >= 0 }
  }

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/deals/yearly-statistics?yearsToShow=3`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setStats(data)
        if (data.years && data.years.length > 0) {
          setSelectedYear(data.years[0].year)
        }
      }
    } catch (error) {
      console.error('Error fetching yearly statistics:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  const selectedYearData = stats?.years.find(y => y.year === selectedYear)
  const selectedYearIndex = stats?.years.findIndex(y => y.year === selectedYear) ?? -1
  const previousYearData = selectedYearIndex >= 0 && selectedYearIndex < (stats?.years.length ?? 0) - 1
    ? stats?.years[selectedYearIndex + 1]
    : null

  return (
    <BaseWidget
      title={t('dashboard.widgets.yearlyStatistics')}
      icon={<CalendarIcon className="h-5 w-5" />}
      accent="analytics"
      variant="gradient"
      className={className}
      loading={loading || !stats}
      minimalDisplay={isMinimal}
    >
      {isMinimal && selectedYearData ? (
        // Minimal display - optimized for larger widget size
        <div className="space-y-3 h-full flex flex-col justify-center py-2">
          {/* Year Selector */}
          <div className="flex items-center justify-center gap-2">
            {stats?.years.map((yearData) => (
              <button
                key={yearData.year}
                onClick={() => setSelectedYear(yearData.year)}
                className={`px-4 py-2 rounded-lg text-base font-semibold transition-all ${
                  selectedYear === yearData.year
                    ? 'bg-white text-green-600 shadow-md'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {yearData.year}
              </button>
            ))}
          </div>
          {/* Main Metric */}
          <div className="text-center">
            <div className="text-5xl font-bold text-white tabular-nums">
              {formatCurrency(selectedYearData.totalPaidCommission)}
            </div>
            <div className="text-base text-white/90 mt-2 font-medium">
              {selectedYearData.totalDealsCount} {t('dashboard.widgets.dealsCompleted')}
            </div>
          </div>
          {/* Secondary Metrics */}
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="text-center bg-white/10 rounded-lg py-2">
              <div className="text-xl font-bold text-white tabular-nums">{formatAmount(selectedYearData.totalLoanVolume)}</div>
              <div className="text-xs text-white/70 mt-0.5">{t('dashboard.widgets.totalLoanVolume')}</div>
            </div>
            <div className="text-center bg-white/10 rounded-lg py-2">
              <div className="text-xl font-bold text-white tabular-nums">{selectedYearData.conversionRate.toFixed(1)}%</div>
              <div className="text-xs text-white/70 mt-0.5">{t('dashboard.widgets.conversionRate')}</div>
            </div>
          </div>
        </div>
      ) : (
        // Full display
        <div className="space-y-4 h-full pb-2">
          {/* Year Selector */}
          <div className="flex items-center justify-center space-x-2 pb-2 flex-shrink-0">
            {stats?.years.map((yearData) => (
              <button
                key={yearData.year}
                onClick={() => setSelectedYear(yearData.year)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                  selectedYear === yearData.year
                    ? 'bg-white text-green-600 shadow-md'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {yearData.year}
              </button>
            ))}
          </div>

          {selectedYearData && (
            <div className="space-y-3">
              {/* Total Paid Commission */}
              <div className="bg-white/15 backdrop-blur-sm rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-white/70 font-semibold">{t('dashboard.widgets.totalPaidCommission')}</p>
                  {previousYearData && (
                    <GrowthIndicator
                      current={selectedYearData.totalPaidCommission}
                      previous={previousYearData.totalPaidCommission}
                    />
                  )}
                </div>
                <p className="text-3xl font-bold text-white tabular-nums">
                  {formatCurrency(selectedYearData.totalPaidCommission)}
                </p>
                <p className="text-xs text-white/60 mt-1">
                  {selectedYearData.totalDealsCount} {t('dashboard.widgets.dealsCompleted')}
                </p>
              </div>

              {/* Loan Volume */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-white/70">{t('dashboard.widgets.totalLoanVolume')}</p>
                  {previousYearData && (
                    <GrowthIndicator
                      current={selectedYearData.totalLoanVolume}
                      previous={previousYearData.totalLoanVolume}
                      small
                    />
                  )}
                </div>
                <p className="text-2xl font-bold text-white tabular-nums">
                  {formatAmount(selectedYearData.totalLoanVolume)}
                </p>
              </div>

              {/* Conversion Rate */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-white/70">{t('dashboard.widgets.leadConversionRate')}</p>
                  {previousYearData && (
                    <GrowthIndicator
                      current={selectedYearData.conversionRate}
                      previous={previousYearData.conversionRate}
                      small
                    />
                  )}
                </div>
                <div className="flex items-baseline space-x-2">
                  <p className="text-2xl font-bold text-white tabular-nums">
                    {selectedYearData.conversionRate}%
                  </p>
                  <p className="text-xs text-white/60">
                    ({selectedYearData.totalDealsCreated}/{selectedYearData.totalLeadsCreated} {t('dashboard.widgets.leads')})
                  </p>
                </div>
              </div>

              {/* Activity Summary */}
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                <p className="text-xs text-white/70 mb-2">{t('dashboard.widgets.activitySummary')}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-sm text-white/60">{t('dashboard.widgets.leadsCreated')}</p>
                    <p className="text-xl font-semibold text-white tabular-nums">
                      {selectedYearData.totalLeadsCreated}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-white/60">{t('dashboard.widgets.dealsCreated')}</p>
                    <p className="text-xl font-semibold text-white tabular-nums">
                      {selectedYearData.totalDealsCreated}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </BaseWidget>
  )
}

// Growth Indicator Component
const GrowthIndicator: React.FC<{ current: number; previous: number; small?: boolean }> = ({ 
  current, 
  previous,
  small = false 
}) => {
  const { percentage, isPositive } = React.useMemo(() => {
    if (previous === 0) {
      return { percentage: current > 0 ? 100 : 0, isPositive: current > 0 }
    }
    const growth = ((current - previous) / previous) * 100
    return { percentage: Math.abs(growth), isPositive: growth >= 0 }
  }, [current, previous])

  if (percentage === 0) return null

  const sizeClass = small ? 'text-[10px]' : 'text-xs'
  const iconSize = small ? 12 : 14

  return (
    <div className={`flex items-center space-x-1 ${isPositive ? 'text-green-300' : 'text-red-300'}`}>
      {isPositive ? (
        <TrendingUpIcon size={iconSize} />
      ) : (
        <TrendingDownIcon size={iconSize} />
      )}
      <span className={`font-semibold ${sizeClass}`}>
        {percentage.toFixed(0)}%
      </span>
    </div>
  )
}

export default YearlyStatisticsWidget

